import { store, store$ } from '@redux/store';
import { distinctUntilChanged, map } from 'rxjs';
import fastdom from 'fastdom';
import { selectProductAndResultType , selectLocationType } from '../redux/selectors';
import ProductType from '../enums/productType';
import ResultType from '../enums/resultType';
import LocationType from '../enums/locationType';
import { searchParametersSlice } from '../redux/slices';
import getDefaultResultType from '../services/resultTypeService';
import getDefaultProductType from '../services/productTypeService';

export default class CustomHousingType {
  constructor() {
    this.selector = '[data-housing-type="custom"]';
    store$.pipe(map(selectProductAndResultType), distinctUntilChanged())
      .subscribe(this.onProductAndResultTypeChange.bind(this));

    store$.pipe(
      map(selectLocationType),
      distinctUntilChanged(),
    ).subscribe(this.onSelectLocationTypeChange.bind(this));
  }

  /**
   * The dispatchAction function dispatches a fetchCustom action with the payload obtained from the
   * getSearchCommands function.
   */
  dispatchFetchResultAction() {
    const payload = this.getSearchCommands();
    store.dispatch(searchParametersSlice.actions.fetchCustom(payload));
  }

  /**
   * Gets the node element of luxury box.
   * @returns {Node} element.
   */
  getNode() {
    return document.querySelector(this.selector);
  }

  /**
   * getSearchCommands: Gets the search command property of this Housing Box.
   * @returns The search command property string.
   */
  getSearchCommands() {
    const searchCommandPropertyStructure = {
      productType: this.productTypeToApply,
      resultType: this.resultTypeToApply,
    };

    return searchCommandPropertyStructure;
  }

  /**
   * The function `onProductAndResultTypeChange` checks the `resultType` and `productType` to be able to update
   * the result and product type that the box should apply.
   * @param productAndResultType - It represents the object with the product and result type.
   */
  onProductAndResultTypeChange(productAndResultType) {
    const { productType, resultType } = productAndResultType;
    this.toggleBox(productType);

    // We have to open the home result type when this button is clicked. In other words, this is when the button
    // is not actived and it was clicked, so we should open the Custom Homes search.
    if (productType !== ProductType.Custom.toString() && productType !== ProductType.BuildOnYourLot.toString()) {
      this.resultTypeToApply = ResultType.Homes.toString();
      this.productTypeToApply = ProductType.Custom.toString();
      return;
    }

    // Otherwise, it means the custom/build on your lot search are actived, so if the button is clicked, we should
    // use the default result type when button is clicked again to deactivate the custom/build on your lot search.
    this.resultTypeToApply = getDefaultResultType(resultType);
    this.productTypeToApply = getDefaultProductType(productType, resultType);
  }

  toggleBox(productType) {
    const node = this.getNode();
    if (!node) { return; }
    if (productType === ProductType.BuildOnYourLot.toString() || productType === ProductType.Custom.toString()) {
      node.selected = true;
    } else {
      node.selected = false;
    }
  }

  /**
   * Shows or hides the "Custom Build Homes" option based on location type.
   * Business rule: hidden on State SRP, visible on all other location types.
   * @param {string} locationType - Current location type from Redux store.
   */
  onSelectLocationTypeChange(locationType) {
    const isStateLevelSearch = locationType === LocationType.State.toString();
    fastdom.mutate(() => {
      const node = this.getNode();
      if (!node) { return; }
      if (isStateLevelSearch) {
        node.setAttribute('hidden', '');
        // Reset the select visually if the custom option is currently selected.
        // The resetCustomProductTypeOnStateSearch epic handles the Redux state reset;
        // this provides immediate DOM feedback while the Redux cycle processes.
        if (node.selected) {
          node.selected = false;
          const allOption = node.closest('[data-housing-select]')?.querySelector('[data-is-opt-all="true"]');
          if (allOption) { allOption.selected = true; }
        }
      } else {
        node.removeAttribute('hidden');
      }
    });
  }
}
